import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ColorName } from '@panaya/ui/styles-to-code';
import type { IconName, Icon } from '@panaya/ui/p-icon';
import type { IAlertData } from './alert-data.interface';
import { AlertType } from './alert-type.enum';

type AlertIcon = Required<Pick<Icon, 'name' | 'color'>>;

// The Record type enforces that we don't forget to provide a default config for any of the alert types
const DefaultIconConfigMap: Record<AlertType, AlertIcon> = {
    [AlertType.Primary]: { name: 'bell', color: 'status-default-unknown' },
    [AlertType.Info]: { name: 'bell', color: 'base-dark' },
    [AlertType.Secondary]: { name: 'bell', color: 'base-dark' },
    [AlertType.Medium]: { name: 'bell', color: 'base-dark' },
    [AlertType.Success]: { name: 'check-circle', color: 'base-dark' },
    [AlertType.Warning]: { name: 'alert-triangle', color: 'status-default-high' },
    [AlertType.Error]: { name: 'x-circle', color: 'status-default-high' }
};

const DefaultAlertType: AlertType = AlertType.Info;

@Component({
    selector: 'p-alert',
    template: `
        <div class="p-alert-container" [ngClass]="{ 'p-alert-center': centralize }">
            <p-icon
                *ngIf="shouldShowIcon"
                class="p-alert-icon"
                [name]="iconName"
                [size]="data.icon?.size || 'md'"
                [color]="iconColor"
            ></p-icon>
            <ng-content select="[slot=alertText]"></ng-content>
            <span class="p-alert-message-main" *ngIf="data?.msgMain" [innerHtml]="data.msgMain || '' | translate"> </span>
            <a
                class="p-alert-action"
                *ngIf="data?.actionText !== undefined"
                [href]="data.linkWithParams || data.linkUrl"
                (click)="onAction($event)"
                [target]="'_blank'"
            >
                {{ data.actionText || '' | translate }}
            </a>
            <span class="p-alert-message-sub p-paragraph-3" *ngIf="data?.msgSub">
                {{ data.msgSub }}
            </span>
            <button class="p-alert-close hidden-text" *ngIf="!data?.hideCloseBtn" (click)="closeClick.emit()">
                {{ data.closeBtnName }}
                <p-icon class="p-alert-close-icon" name="x" size="sm"></p-icon>
            </button>
        </div>
    `,
    styleUrls: ['./p-alert.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class PAlertComponent {
    @Input() public data: IAlertData = {} as IAlertData;
    @Input() public centralize = false;

    @Output() public readonly closeClick = new EventEmitter<void>();

    constructor(private readonly router: Router, private readonly route: ActivatedRoute) {}

    public onAction(event: MouseEvent): void {
        if (!this.data.targetNew) {
            event.preventDefault();
            event.stopPropagation();

            const { queryParams, linkUrl } = this.data;
            if (linkUrl) {
                this.router.navigate([linkUrl], { relativeTo: this.route, queryParams });
            }
        }
        if (this.data.action) {
            this.data.action();
        }
    }

    public get shouldShowIcon(): boolean {
        // Icon should show if a definition is available (via config or default values) and no hide config has been provided
        return !this.data.hideIcon && !!this.iconName;
    }

    public get iconName(): IconName {
        return this.getIconPropertyOrDefault('name');
    }
    public get iconColor(): ColorName {
        return this.getIconPropertyOrDefault('color');
    }

    private getIconPropertyOrDefault<Prop extends keyof AlertIcon>(propertyName: Prop): AlertIcon[Prop] {
        return (
            (this.data.icon?.[propertyName] as AlertIcon[Prop]) ?? DefaultIconConfigMap[this.data.type ?? DefaultAlertType][propertyName]
        );
    }
}
